import { useGomakeTheme } from "@/hooks/use-gomake-thme";
import { adaptLeft } from "@/utils/adapter";
import { FONT_FAMILY } from "@/utils/font-family";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

const useStyle = () => {
  const { primaryColor, secondColor } = useGomakeTheme();
  const { t } = useTranslation();
  const clasess = useMemo(() => {
    return {
      insideStyle: {
        width: "min(1180px, 96vw)",
        minWidth: "340px",
        borderRadius: 8,
        height: "min(900px, calc(100vh - 48px))",
        maxHeight: "min(900px, calc(100vh - 48px))",
        backgroundColor: "#F6F6F6",
        padding: "28px 32px",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column" as "column",
      },
      modalContent: {
        display: "flex",
        flexDirection: "column" as "column",
        flex: "1 1 0%",
        minHeight: 0,
        height: "auto",
        maxHeight: "none",
        width: "100%",
        overflow: "hidden",
        overflowY: "hidden" as "hidden",
        overflowX: "hidden" as "hidden",
      },
      firstContainer: {
        position: "absolute" as "absolute",
        ...adaptLeft(t("direction"), 32),
        top: 28,
      },
      headerContainer: {
        display: "flex",
        flexDirection: "row" as "row",
        justifyContent: "flex-start",
        alignItems: "center",
        gap: 10,
        flexWrap: "wrap" as "wrap",
      },
      bodyContainer: {
        display: "flex",
        flexDirection: "column" as "column",
        flex: "1 1 0%",
        minHeight: 0,
        width: "100%",
        overflow: "hidden",
        marginBottom: 12,
        marginTop: 12,
      },
      filtersBar: {
        display: "flex",
        flexDirection: "row" as "row",
        flexWrap: "wrap" as "wrap",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 10,
        flexShrink: 0,
        width: "100%",
      },
      footerContainer: {
        display: "flex",
        flexDirection: "row" as "row",
        width: "100%",
        justifyContent: "space-between",
        alignItems: "center",
        flexShrink: 0,
      },
      switchContainer: {
        display: "flex",
        flexDirection: "row" as "row",
        justifyContent: "flex-start",
        alignItems: "center",
      },
      switchlabel: {
        ...FONT_FAMILY.Lexend(400, 12),
        color: "#000",
      },
      switchlabelSelected: {
        ...FONT_FAMILY.Lexend(400, 12),
        color: secondColor(500),
      },
      contentRow: {
        display: "flex",
        flexDirection: "row" as "row",
        flexWrap: "nowrap" as "nowrap",
        alignItems: "stretch",
        gap: 16,
        width: "100%",
        marginTop: 16,
        flex: "1 1 0%",
        minHeight: 0,
        overflow: "hidden",
      },
      cardsColumn: {
        flex: "1 1 auto",
        minWidth: 0,
        minHeight: 0,
        height: "100%",
        overflowY: "auto" as "auto",
        overflowX: "hidden" as "hidden",
      },
      mainContainer: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
        alignItems: "stretch",
        width: "100%",
        gap: 12,
        marginBottom: 20,
        padding: 5,
      },
      shapeContainer: {
        position: "relative" as "relative",
        display: "flex",
        flexDirection: "column" as "column",
        justifyContent: "flex-start",
        alignItems: "stretch",
        width: "100%",
        minHeight: 0,
        backgroundColor: "#FFFFFF",
        cursor: "pointer",
        borderRadius: 12,
        border: "2px solid #E6E6EF",
        boxShadow: "0px 4px 20px 0px rgba(0, 0, 0, 0.06)",
        overflow: "hidden",
        textAlign: "start" as "start",
      },
      fixdCard: {
        display: "flex",
        flexDirection: "column" as "column",
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
        height: "100%",
        minHeight: 220,
        backgroundColor: "white",
        gap: 8,
        cursor: "pointer",
        boxShadow: "0px 4px 20px 0px rgba(0, 0, 0, 0.08)",
        padding: "20px 16px",
        borderRadius: 12,
        border: "2px solid transparent",
        textAlign: "center" as "center",
      },
      fixdCardSelected: {
        display: "flex",
        flexDirection: "column" as "column",
        justifyContent: "center" as "center",
        alignItems: "center" as "center",
        width: "100%",
        height: "100%",
        minHeight: 220,
        backgroundColor: "white",
        gap: 8,
        cursor: "pointer",
        boxShadow: "0px 4px 20px 0px rgba(0, 0, 0, 0.08)",
        padding: "20px 16px",
        borderRadius: 12,
        border: `2px solid ${primaryColor(500)}`,
        textAlign: "center" as "center",
      },
      cardItemStyle: {
        display: "flex",
        flexDirection: "column" as "column",
        justifyContent: "center",
        alignItems: "center",
        border: "1px solid #FFFF",
        width: "100%",
        height: "100%",
        gap: 15,
      },
      cardIconStyle: {
        height: 55,
      },
      cardNameStyle: {
        ...FONT_FAMILY.Lexend(500, 18),
        color: "#FFFFFF",
        textAlign: "center" as "center",
      },
      cardDescriptionStyle: {
        ...FONT_FAMILY.Lexend(400, 13),
        color: "rgba(255, 255, 255, 0.88)",
        textAlign: "center" as "center",
        lineHeight: "18px",
      },
      shapeSelectedContainer: {
        position: "relative" as "relative",
        display: "flex",
        flexDirection: "column" as "column",
        justifyContent: "flex-start",
        alignItems: "stretch",
        width: "100%",
        minHeight: 0,
        backgroundColor: "#FFFFFF",
        borderRadius: 12,
        border: `2px solid ${primaryColor(500)}`,
        boxShadow: "0px 6px 24px 0px rgba(80, 79, 161, 0.16)",
        cursor: "pointer",
        overflow: "hidden",
        textAlign: "start" as "start",
      },
      shapeFocus: {
        outline: `2px solid ${primaryColor(500)}`,
        outlineOffset: 2,
      },
      imageWrap: {
        position: "relative" as "relative",
        width: "100%",
        height: 112,
        backgroundColor: "#FAFAFB",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderBottom: "1px solid #F0F0F4",
        flexShrink: 0,
      },
      shapeImage: {
        width: "100%",
        height: "100%",
        objectFit: "contain" as "contain",
        padding: 8,
      },
      imagePlaceholder: {
        ...FONT_FAMILY.Lexend(500, 12),
        color: "#8B8BA7",
        textAlign: "center" as "center",
        paddingInline: 12,
      },
      selectedBadge: {
        position: "absolute" as "absolute",
        top: 8,
        insetInlineEnd: 8,
        width: 22,
        height: 22,
        borderRadius: "50%",
        backgroundColor: primaryColor(500),
        color: "#FFFFFF",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      },
      cardBody: {
        display: "flex",
        flexDirection: "column" as "column",
        gap: 8,
        padding: "12px 12px 14px",
        width: "100%",
      },
      titleRow: {
        display: "flex",
        flexDirection: "row" as "row",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 8,
        width: "100%",
      },
      shapeNameStyle: {
        ...FONT_FAMILY.Lexend(600, 16),
        color: primaryColor(500),
        minWidth: 0,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap" as "nowrap",
      },
      skuChip: {
        ...FONT_FAMILY.Lexend(600, 12),
        color: primaryColor(500),
        backgroundColor: "#F1F0FA",
        borderRadius: 8,
        padding: "2px 8px",
        flexShrink: 0,
      },
      specList: {
        display: "flex",
        flexDirection: "column" as "column",
        gap: 6,
        width: "100%",
      },
      specRow: {
        display: "flex",
        flexDirection: "row" as "row",
        alignItems: "flex-start",
        gap: 6,
        width: "100%",
      },
      specIcon: {
        width: 16,
        height: 16,
        flexShrink: 0,
        marginTop: 1,
        color: "#8B8BA7",
      },
      specLabel: {
        ...FONT_FAMILY.Lexend(400, 12),
        color: "#6B7280",
        flex: "1 1 auto",
        minWidth: 0,
      },
      specValue: {
        ...FONT_FAMILY.Lexend(500, 12),
        color: "#1F2430",
        textAlign: "end" as "end",
        flexShrink: 1,
        maxWidth: "55%",
        overflowWrap: "anywhere" as "anywhere",
      },
      hiddenToggle: {
        position: "absolute" as "absolute",
        bottom: 0,
        insetInlineEnd: 4,
        zIndex: 2,
      },
      detailsPanel: {
        flex: "0 1 320px",
        width: "auto",
        maxWidth: 360,
        minWidth: 240,
        alignSelf: "stretch",
        display: "flex",
        flexDirection: "column" as "column",
        gap: 12,
        backgroundColor: "#FFFFFF",
        borderRadius: 12,
        border: "1px solid #E6E6EF",
        boxShadow: "0px 4px 20px 0px rgba(0, 0, 0, 0.06)",
        padding: 16,
        minHeight: 0,
        height: "100%",
        overflowY: "auto" as "auto",
      },
      detailsHeader: {
        display: "flex",
        flexDirection: "row" as "row",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 8,
      },
      detailsTitle: {
        ...FONT_FAMILY.Lexend(600, 16),
        color: primaryColor(500),
        minWidth: 0,
      },
      statusBadgeActive: {
        ...FONT_FAMILY.Lexend(600, 12),
        color: "#1B7F4E",
        backgroundColor: "#E7F6EE",
        borderRadius: 999,
        padding: "4px 8px",
        flexShrink: 0,
      },
      statusBadgeInactive: {
        ...FONT_FAMILY.Lexend(600, 12),
        color: "#4B5563",
        backgroundColor: "#F3F4F6",
        borderRadius: 999,
        padding: "4px 8px",
        flexShrink: 0,
      },
      detailsImageWrap: {
        width: "100%",
        height: 140,
        borderRadius: 8,
        backgroundColor: "#FAFAFB",
        border: "1px solid #F0F0F4",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
      },
      detailsImage: {
        width: "100%",
        height: "100%",
        objectFit: "contain" as "contain",
      },
      detailsList: {
        display: "flex",
        flexDirection: "column" as "column",
        margin: 0,
        width: "100%",
      },
      detailsRow: {
        display: "flex",
        flexDirection: "row" as "row",
        justifyContent: "space-between",
        alignItems: "flex-start",
        gap: 12,
        padding: "8px 0",
        borderBottom: "1px solid #F0F0F4",
      },
      detailsLabel: {
        ...FONT_FAMILY.Lexend(400, 13),
        color: "#6B7280",
        margin: 0,
      },
      detailsValue: {
        ...FONT_FAMILY.Lexend(500, 13),
        color: "#1F2430",
        margin: 0,
        textAlign: "end" as "end",
        overflowWrap: "anywhere" as "anywhere",
      },
      detailsSelectButton: {
        width: "100%",
        height: 40,
        marginTop: "auto",
        backgroundColor: secondColor(500),
        flexShrink: 0,
      },
      btnsContainer: {
        display: "flex",
        flexDirection: "row" as "row",
        justifyContent: "flex-end",
        alignItems: "center",
        width: "100%",
        gap: 16,
        marginTop: 8,
      },
      customizeBtnStyle: {
        width: "15%",
        height: 40,
      },
      chooseBtnStyle: {
        width: "fit-content",
        minWidth: 140,
        height: 40,
        backgroundColor: secondColor(500),
      },
    };
  }, [t, primaryColor, secondColor]);
  return {
    clasess,
  };
};
export { useStyle };
